import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { LOGO_URL } from "../lib/data";
import { TID } from "../lib/testIds";

const LINKS = [
  { href: "/#services", label: "Services", id: TID.navServices },
  { href: "/#work", label: "Work", id: TID.navWork },
  { href: "/#manifesto", label: "Manifesto", id: TID.navManifesto },
  { href: "/sponsorships", label: "Sponsor", id: "nav-link-sponsor" },
  { href: "/#contact", label: "Contact", id: TID.navContact },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href) => (e) => {
    setOpen(false);
    // Route-only link (e.g. /sponsorships)
    if (!href.includes("#")) {
      e.preventDefault();
      navigate(href);
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }
    // "/#anchor" — if we're on home, smooth-scroll; otherwise navigate to home + anchor
    const [path, hash] = href.split("#");
    if (location.pathname === (path || "/")) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      e.preventDefault();
      navigate(`${path || "/"}#${hash}`);
    }
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-black/55 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid={TID.navLogo}
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt="Event Monk"
            className="h-9 w-9 md:h-10 md:w-10 object-contain"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-bold text-[0.95rem] tracking-tight">
              EVENT MONK
            </span>
            <span className="font-mono-em text-[0.62rem] tracking-[0.24em] text-white/50 uppercase mt-1">
              Events Reimagined
            </span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={handleNavClick(l.href)}
              data-testid={l.id}
              className="em-underline-link font-mono-em text-[0.72rem] tracking-[0.22em] uppercase text-white/70 hover:text-white"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/#contact"
            onClick={handleNavClick("/#contact")}
            data-testid={TID.navCta}
            className="em-btn em-btn-primary hidden sm:inline-flex"
          >
            Start a Project <ArrowUpRight size={16} />
          </a>
          <button
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden h-10 w-10 grid place-items-center border border-white/15 rounded-full"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-black/90 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={handleNavClick(l.href)}
                  className="font-display text-2xl tracking-tight"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="/#contact"
                onClick={handleNavClick("/#contact")}
                className="em-btn em-btn-primary self-start mt-2"
              >
                Start a Project <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
